import {
  BoxGeometry,
  BufferGeometry,
  EdgesGeometry,
  ExtrudeGeometry,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Shape,
  Vector2,
} from 'three';
import { CuboidLocationState3D } from '../../../state/cuboid-location-state-3d';
import { ShapeLocationState3D } from '../../../state/shape-location-state-3d';
import { ObjectWith } from '../../../utils/misc-utils';
import { setRendererLayers } from '../../render-utils';
import { disposeAll, stripedTexture } from '../mesh-utils';
import { ROUTE_RENDERER_LAYERS } from './cuboid-route-helper';

export function getRouteMeshName(cuboidIndex: number, frameNumber: number) {
  return `cuboid-${cuboidIndex}-frameNumber-${frameNumber}`;
}

const pastColor = '#625F5F';
const defaultColor = '#00CBCB';

const markerSize = 0.5;

const allMarkerStates = ['past', 'current', 'future'] as const;
type MarkerState = (typeof allMarkerStates)[number];

export class RouteMarker extends Object3D {
  private static materials: ObjectWith<MeshBasicMaterial>;
  private static stripedMaterials: ObjectWith<MeshBasicMaterial>;
  private static wireframeMaterials: ObjectWith<LineBasicMaterial>;

  private static triangleGeometry: BufferGeometry;
  private static triangleWireframeGeometry: EdgesGeometry;
  private static wallGeometry: BufferGeometry;
  private static wallWireframeGeometry: EdgesGeometry;

  private triangle: Mesh;
  private triangleWireframe: LineSegments;

  private wall: Mesh;
  private wallWireframe: LineSegments;

  constructor(
    private frameNumber: number,
    cuboidIndex: number,
  ) {
    super();

    this.triangle = new Mesh(RouteMarker.triangleGeometry);
    this.triangleWireframe = new LineSegments(
      RouteMarker.triangleWireframeGeometry,
    );

    this.wall = new Mesh(RouteMarker.wallGeometry);
    this.wallWireframe = new LineSegments(RouteMarker.wallWireframeGeometry);

    this.add(
      this.triangle,
      this.triangleWireframe,
      this.wall,
      this.wallWireframe,
    );

    setRendererLayers(ROUTE_RENDERER_LAYERS, this);

    this.name = getRouteMeshName(cuboidIndex, this.frameNumber);
    this.userData = { meshType: 'RouteMarker' };
  }

  // This method is here to support tests, since our framework tears down
  // services but not script constants. Therefore, we need to initialize
  // the materials on every service load
  static instantiateResources() {
    RouteMarker.materials = buildMaterials();
    RouteMarker.stripedMaterials = buildStripedMaterials();
    RouteMarker.wireframeMaterials = buildWireframeMaterials();

    RouteMarker.triangleGeometry = buildTriangleGeometry();
    RouteMarker.triangleWireframeGeometry = new EdgesGeometry(
      RouteMarker.triangleGeometry,
    );

    RouteMarker.wallGeometry = buildWallGeometry();
    RouteMarker.wallWireframeGeometry = new EdgesGeometry(
      RouteMarker.wallGeometry,
    );
  }

  static dispose() {
    disposeAll(RouteMarker.materials);
    disposeAll(RouteMarker.stripedMaterials);
    disposeAll(RouteMarker.wireframeMaterials);

    RouteMarker.triangleGeometry.dispose();
    RouteMarker.triangleWireframeGeometry.dispose();

    RouteMarker.wallGeometry.dispose();
    RouteMarker.wallWireframeGeometry.dispose();
  }

  updateLocation(
    location: CuboidLocationState3D,
    currentFrameNumber: number,
    isKeyLocation: boolean,
    previousLocationWasVisible: boolean,
  ) {
    this.position.fromArray(location.position);
    this.rotation.fromArray(location.rotation);

    setRendererLayers(ROUTE_RENDERER_LAYERS, this);

    if (location.visibility === 0 && previousLocationWasVisible) {
      this.add(this.wall, this.wallWireframe);
    } else {
      this.remove(this.wall, this.wallWireframe);
    }

    if (location.visibility === 0 && !isKeyLocation) {
      setRendererLayers([], this);
    }

    const materials = this.getMaterials(location, currentFrameNumber);

    this.triangle.material = materials.marker;
    this.wall.material = materials.marker;
    this.triangleWireframe.material = materials.wireframe;
    this.wallWireframe.material = materials.wireframe;
  }

  private getMaterials(
    location: ShapeLocationState3D,
    currentFrameNumber: number,
  ) {
    let key: MarkerState;
    if (location.frame_number === currentFrameNumber) {
      key = 'current';
    } else {
      key = this.frameNumber < currentFrameNumber ? 'past' : 'future';
    }

    let marker: MeshBasicMaterial;
    if (location.visibility === 0.5) {
      marker = RouteMarker.stripedMaterials[key];
    } else {
      marker = RouteMarker.materials[key];
    }

    const wireframe = RouteMarker.wireframeMaterials[key];
    return { marker, wireframe };
  }
}

function buildMaterials() {
  const materials: ObjectWith<MeshBasicMaterial> = {};
  allMarkerStates.forEach((markerState) => {
    materials[markerState] = new MeshBasicMaterial({
      color: markerState === 'past' ? pastColor : defaultColor,
      opacity: markerState === 'current' ? 1 : 0.5,
      transparent: markerState !== 'current',
    });
  });
  return materials;
}

function buildStripedMaterials() {
  const materials: ObjectWith<MeshBasicMaterial> = {};
  allMarkerStates.forEach((markerState) => {
    materials[markerState] = new MeshBasicMaterial({
      color: markerState === 'past' ? pastColor : defaultColor,
      transparent: true,
      opacity: 0.7,
      map: stripedTexture,
    });
  });
  return materials;
}

function buildWireframeMaterials() {
  const pastWireframeColor = '#CFCFCF';
  const defaultWireframeColor = '#A1FFFF';

  const materials: ObjectWith<LineBasicMaterial> = {};
  allMarkerStates.forEach((markerState) => {
    materials[markerState] = new LineBasicMaterial({
      color:
        markerState === 'past' ? pastWireframeColor : defaultWireframeColor,
    });
  });
  return materials;
}

function buildTriangleGeometry() {
  const heightSize = markerSize;
  const baseSize = markerSize * 0.75;

  const triangleShape = new Shape().setFromPoints([
    new Vector2(baseSize / 2, 0),
    new Vector2(0, heightSize),
    new Vector2(baseSize, heightSize),
  ]);

  const depth = markerSize / 5;
  const geometry = new ExtrudeGeometry(triangleShape, {
    depth,
    steps: 1,
    bevelEnabled: false,
  });

  // make 0,0,0 be the centroid of the triangle
  geometry.translate(-baseSize / 2, -heightSize / 2, -depth / 2);

  return geometry;
}

function buildWallGeometry() {
  const thickness = markerSize / 10;
  const geometry = new BoxGeometry(markerSize, thickness, markerSize);
  geometry.translate(0, -(markerSize / 2 + thickness / 2), 0);
  return geometry;
}
